import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Star, Plus, Save, Trash2, ArrowUp, ArrowDown, Loader2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { author: "", author_role: "", rating: 5, body: "", source_url: "", published_at: "", sort_order: 100, is_visible: true };

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews").then((r) => setReviews(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      author: r.author || "",
      author_role: r.author_role || "",
      rating: r.rating || 5,
      body: r.body || "",
      source_url: r.source_url || "",
      published_at: r.published_at || "",
      sort_order: r.sort_order ?? 100,
      is_visible: r.is_visible !== false,
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.body.trim()) { toast.error("Author and body are required."); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/reviews/${editingId}`, form);
        toast.success("Review updated.");
      } else {
        await api.post(`/admin/reviews`, form);
        toast.success("Review added.");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save.");
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this review by ${r.author}?`)) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted."); if (editingId === r.id) cancelEdit(); load(); }
    catch { toast.error("Could not delete."); }
  };

  const toggleVisible = async (r) => {
    try { await api.put(`/admin/reviews/${r.id}`, { is_visible: !r.is_visible }); load(); }
    catch { toast.error("Could not toggle."); }
  };

  const move = async (r, dir) => {
    const next = (r.sort_order ?? 100) + (dir === "up" ? -15 : 15);
    try { await api.put(`/admin/reviews/${r.id}`, { sort_order: next }); load(); } catch {}
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-reviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
        <div>
          <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">Customer voice</p>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] font-semibold tracking-tight mt-1">Reviews</h1>
          <p className="text-sm text-[#447694] mt-1.5">Paste real Google reviews here. They'll show on the homepage testimonial strip. Until your Google Places API key is in place, this is the source of truth.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-2 bnp-card p-6 space-y-3.5 self-start" data-testid="admin-review-form">
          <div className="flex items-center justify-between">
            <p className="font-display font-semibold text-[#04273d]">{editingId ? "Edit review" : "Add a review"}</p>
            {editingId && <button type="button" onClick={cancelEdit} className="text-xs text-[#447694] hover:text-[#04273d] underline">Cancel</button>}
          </div>

          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Author *</label>
            <input required value={form.author} onChange={(e) => set("author", e.target.value)} className="bnp-input" placeholder="Jane Smith" data-testid="admin-review-author" />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Company / role</label>
            <input value={form.author_role} onChange={(e) => set("author_role", e.target.value)} className="bnp-input" placeholder="e.g. Owner, Crown Hair Studio" data-testid="admin-review-role" />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Rating *</label>
            <div className="flex gap-1.5" data-testid="admin-review-rating">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => set("rating", n)} className="p-1">
                  <Star size={26} className={n <= form.rating ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#cbd5e1]"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Review text *</label>
            <textarea required rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} className="bnp-input" placeholder="Paste the review text here." data-testid="admin-review-body" />
          </div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Date</label>
              <input value={form.published_at} onChange={(e) => set("published_at", e.target.value)} className="bnp-input" placeholder="March 2025" data-testid="admin-review-date" />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Order (low=first)</label>
              <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value || "100", 10))} className="bnp-input" data-testid="admin-review-order" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5">Source URL (optional Google review link)</label>
            <input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} className="bnp-input" placeholder="https://g.co/kgs/..." data-testid="admin-review-url" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#447694]">
            <input type="checkbox" checked={form.is_visible} onChange={(e) => set("is_visible", e.target.checked)} data-testid="admin-review-visible" />
            Show this review on the public site
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-review-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : (editingId ? <Save size={14} /> : <Plus size={14} />)}
            {editingId ? "Save changes" : "Add review"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="bnp-card p-8 text-center text-[#447694]">Loading…</div>
          ) : reviews.length === 0 ? (
            <div className="bnp-card p-10 text-center">
              <Star size={28} className="text-[#9ac7f7] mx-auto" />
              <p className="font-display text-[#04273d] mt-3 font-semibold">No reviews yet</p>
              <p className="text-sm text-[#447694] mt-1">Paste your first Google review with the form on the left.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="admin-review-list">
              {reviews.map((r, i) => (
                <li key={r.id} className={`bnp-card p-5 ${!r.is_visible ? "opacity-60" : ""}`} data-testid={`admin-review-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-display font-semibold text-[#04273d] text-base">{r.author}</p>
                        <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#cbd5e1]"} />)}</div>
                      </div>
                      {r.author_role && <p className="text-xs text-[#447694] mt-0.5">{r.author_role}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => toggleVisible(r)} className="p-2 rounded-full hover:bg-[#9ac7f7]/15 text-[#04273d]" title={r.is_visible ? "Hide" : "Show"} data-testid={`admin-review-toggle-${r.id}`}>
                        {r.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button type="button" onClick={() => move(r, "up")} disabled={i===0} className="p-2 rounded-full hover:bg-[#9ac7f7]/15 text-[#04273d] disabled:opacity-30" title="Up"><ArrowUp size={14} /></button>
                      <button type="button" onClick={() => move(r, "down")} disabled={i===reviews.length-1} className="p-2 rounded-full hover:bg-[#9ac7f7]/15 text-[#04273d] disabled:opacity-30" title="Down"><ArrowDown size={14} /></button>
                      <button type="button" onClick={() => startEdit(r)} className="btn-ghost text-xs">Edit</button>
                      <button type="button" onClick={() => remove(r)} className="p-2 rounded-full hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <p className="text-sm text-[#04273d] italic leading-relaxed">&ldquo;{r.body}&rdquo;</p>
                  {(r.published_at || r.source_url) && (
                    <div className="text-[11px] text-[#447694] mt-2 flex items-center gap-2">
                      {r.published_at && <span>{r.published_at}</span>}
                      {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:text-[#04273d]">Source <ExternalLink size={10} /></a>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
